import { CSSProperties, useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faFileExcel,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

import DataTable from "../../common/components/DataTable";
import { AppDispatch, RootState } from "../template/store";
import {
  AnalyticsDashboardResponse,
  exportSezDashboard,
  getSezDashboard,
} from "./SezActions";
import dataTableBgImg from "../../../assets/img/illustrations/cornerRight-1.png";

const label = (key: string) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
const reportLabel = (title: string) => title.split("—")[0].trim();
const isNumericKey = (key: string) => /amt|amount|count|total/i.test(key);
const isDateKey = (key: string) => /date|_dt$|_on$/i.test(key);
const PAGE_SIZE = 20;

const REPORT_DEFINITIONS = [
  {
    id: "top-payors",
    title: "Top Payors — Pending SEZ Certificates",
    legacyKey: "top_payors",
    gridName: "SEZ_REPORT_TOP_PAYORS",
    uniqueFilterKey: "sezReportTopPayors",
    addCurrency: false,
  },
  {
    id: "unmatched-certs",
    title: "Certificate Received — Invoice Data Not Yet Matched",
    legacyKey: "certificate_not_matched",
    gridName: "SEZ_REPORT_UNMATCHED_CERTS",
    uniqueFilterKey: "sezReportUnmatchedCerts",
    addCurrency: true,
  },
] as const;

type ReportId = (typeof REPORT_DEFINITIONS)[number]["id"];

const getReportData = (
  tables: AnalyticsDashboardResponse["tables"] | undefined,
  title: string,
  legacyKey: (typeof REPORT_DEFINITIONS)[number]["legacyKey"]
) => {
  const titledRows = tables?.[title];
  const legacyTable = tables?.[legacyKey];

  return {
    title:
      !Array.isArray(legacyTable) && legacyTable?.title
        ? legacyTable.title
        : title,
    rows: Array.isArray(titledRows)
      ? titledRows
      : !Array.isArray(legacyTable)
      ? legacyTable?.rows ?? []
      : [],
  };
};

const buildColumns = (rows: any[]) => {
  const keys = Object.keys(rows[0] || {}).filter((key) => key !== "id");
  const nameCountReport =
    keys.length === 2 &&
    keys.some((key) => key.toLowerCase() === "name") &&
    keys.some((key) => key.toLowerCase() === "count");

  return [
    { accessor: "id", Header: "id", csv: false },
    ...keys.map((key) => {
      const numeric = isNumericKey(key);
      const rightAlign = numeric && !nameCountReport;
      const certificateIssueDate = key === "certificate_issue_date";
      const width = nameCountReport
        ? key.toLowerCase() === "name"
          ? 288
          : 192
        : keys.length <= 3
        ? Math.floor(480 / keys.length)
        : numeric
        ? 110
        : 160;

      return {
        accessor: key,
        Header: label(key),
        width,
        minWidth: nameCountReport ? 0 : numeric ? 90 : 130,
        maxWidth: nameCountReport ? width : numeric ? 140 : 240,
        columnFilterType: isDateKey(key)
          ? "date-range"
          : numeric
          ? "number-range"
          : "search",
        headerClass: certificateIssueDate
          ? "sez-certificate-issue-date"
          : undefined,
        cellProps: {
          className: `${rightAlign ? "text-end" : ""} ${
            certificateIssueDate ? "sez-certificate-issue-date" : ""
          }`.trim(),
        },
        headerProps: { className: rightAlign ? "text-end" : undefined },
      };
    }),
  ];
};

type RangeKey = "30" | "90" | "180";

const RANGE_TO_API: Record<RangeKey, string> = {
  "30": "30D",
  "90": "3M",
  "180": "6M",
};

const RANGE_OPTIONS: { value: RangeKey; label: string }[] = [
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 3 months" },
  { value: "180", label: "Last 6 months" },
];
const INITIAL_PAGES: Record<ReportId, number> = {
  "top-payors": 1,
  "unmatched-certs": 1,
};

type ReportConfig = {
  id: ReportId;
  title: string;
  gridName: string;
  uniqueFilterKey: string;
  columns: any[];
  rows: any[];
  page: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
  hasNext: boolean;
  serverPagination: boolean;
  onPageChange: (page: number) => void;
};

export default function ReportsDashboard() {
  const dispatch = useDispatch<AppDispatch>();
  const apiData = useSelector(
    (state: RootState) => state.sezDashboard.analyticsData
  ) as AnalyticsDashboardResponse | null;
  const loading = useSelector(
    (state: RootState) => state.sezDashboard.analyticsLoading
  );
  const apiError = useSelector(
    (state: RootState) => state.sezDashboard.analyticsError
  );
  const [range, setRange] = useState<RangeKey>("30");
  const [activeReportId, setActiveReportId] =
    useState<ReportId>("top-payors");
  const [pages, setPages] = useState(INITIAL_PAGES);

  useEffect(() => {
    dispatch(
      getSezDashboard({
        dateRange: RANGE_TO_API[range],
        topPayorsPage: pages["top-payors"],
        topPayorsPageSize: PAGE_SIZE,
        certificatesPage: pages["unmatched-certs"],
        certificatesPageSize: PAGE_SIZE,
      })
    );
  }, [dispatch, range, pages]);

  const reports: ReportConfig[] = REPORT_DEFINITIONS.map((definition) => {
    const table = getReportData(
      apiData?.tables,
      definition.title,
      definition.legacyKey
    );
    const rows = table.rows.map((row: any, id: number) => ({
      id,
      ...row,
      ...(definition.addCurrency
        ? { currency: row.currency ?? "INR" }
        : {}),
    }));
    const pagination = apiData?.tables_pagination?.[definition.title];

    return {
      ...definition,
      title: table.title,
      columns: buildColumns(rows),
      rows,
      page: pagination?.page ?? pages[definition.id],
      pageSize: pagination?.page_size ?? PAGE_SIZE,
      totalPages: pagination?.total_pages ?? 1,
      totalRecords: pagination?.total_records ?? rows.length,
      hasNext: pagination?.has_next ?? false,
      serverPagination: pagination?.pagination_enabled ?? false,
      onPageChange: (page) =>
        setPages((current) => ({ ...current, [definition.id]: page })),
    };
  });

  const activeReport =
    reports.find((report) => report.id === activeReportId) ?? reports[0];

  if ((loading || apiError) && !apiData) {
    return (
      <div className="d-flex align-items-center justify-content-center w-100 h-100">
        <span className={apiError ? "text-danger" : "text-muted"}>
          {apiError || "Loading reports…"}
        </span>
      </div>
    );
  }

  return (
    <div
      className="sez-reports-page d-flex flex-column w-100 overflow-hidden"
      style={{
        height: "calc(100vh - 4.3125rem)",
        padding: "6px 0 8px",
        background: "#edf2f9",
      }}
    >
      <ReportTable
        report={activeReport}
        reports={reports}
        range={range}
        onRangeChange={(nextRange) => {
          setPages(INITIAL_PAGES);
          setRange(nextRange);
        }}
        onReportChange={setActiveReportId}
      />
    </div>
  );
}

function ReportTable({
  report,
  reports,
  range,
  onRangeChange,
  onReportChange,
}: {
  report: ReportConfig;
  reports: ReportConfig[];
  range: RangeKey;
  onRangeChange: (range: RangeKey) => void;
  onReportChange: (reportId: ReportId) => void;
}) {
  const hasRows = report.rows.length > 0;
  const [exporting, setExporting] = useState(false);

  const exportReport = async () => {
    try {
      setExporting(true);
      const exportType =
        report.id === "top-payors" ? "toppayors" : "certificate";
      const response = await exportSezDashboard(
        RANGE_TO_API[range],
        exportType
      );
      const fileName =
        report.id === "top-payors"
          ? "Top Payor Report.xlsx"
          : "Invoice Mismatch Certificate Report.xlsx";
      const contentType = response.headers["content-type"];
      const file = new Blob([response.data], {
        type:
          (typeof contentType === "string" && contentType) ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("File downloaded successfully", { theme: "colored" });
    } catch {
      toast.error("Unable to export reports", { theme: "colored" });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div
      className="card position-relative d-flex flex-column flex-grow-1 overflow-hidden bg-white"
      style={{
        minHeight: 0,
        border: "1px solid #d8e2ef",
        borderRadius: "0.5rem",
        boxShadow: "0 0.25rem 0.75rem rgba(31, 45, 61, 0.12)",
      }}
    >
      <div
        className="bg-holder bg-card"
        style={{ backgroundImage: `url(${dataTableBgImg})` }}
      />
      <style>{`
        .sez-report-table table,
        .sez-report-table thead,
        .sez-report-table tbody,
        .sez-report-table tr { width: 100% !important; min-width: 100% !important; }
        .sez-top-payors-table th:first-child,
        .sez-top-payors-table td:first-child { width: 60% !important; max-width: 60% !important; }
        .sez-top-payors-table th:nth-child(2),
        .sez-top-payors-table td:nth-child(2) { width: 40% !important; max-width: 40% !important; }
        .sez-report-table:not(.sez-top-payors-table) th,
        .sez-report-table:not(.sez-top-payors-table) td {
          width: calc(100% / var(--report-column-count)) !important;
          min-width: 130px !important;
          max-width: none !important;
          box-sizing: border-box;
        }
        .sez-certificate-report th,
        .sez-certificate-report td {
          width: var(--report-other-column-width) !important;
        }
        .sez-certificate-report .sez-certificate-issue-date {
          width: 18% !important;
          min-width: 200px !important;
        }
        .sez-certificate-report th.sez-certificate-issue-date .dropdown-menu-card .card {
          width: min(560px, calc(100vw - 24px)) !important;
        }
        .sez-report-select { flex: 1 1 22rem; min-width: 18rem; max-width: 34rem; }
        .sez-report-select .form-select { min-width: 0; text-overflow: ellipsis; }
        .sez-range-select { width: 170px; }
        .sez-range-select .form-select { width: 100%; min-width: 0; text-overflow: ellipsis; }
        .sez-reports-toolbar .form-select,
        .sez-reports-toolbar .btn { min-height: 31px; }
        .sez-report-table {
          overflow: hidden !important;
        }
        .sez-report-table > .d-flex {
          height: 100% !important;
          min-height: 0;
          margin-bottom: 0 !important;
          overflow: hidden;
        }
        .sez-report-table > .d-flex > .flex-1 {
          min-height: 0;
        }
        .sez-report-table > .d-flex > .mt-2 {
          flex: 0 0 auto;
          min-height: 44px;
          margin-top: 0 !important;
          padding-top: 0.5rem;
          padding-bottom: 0.5rem;
        }
        .sez-report-table .odex-thead {
          position: sticky;
          top: 0;
          z-index: 5;
        }
        .sez-report-table .odex-data-table-root {
          height: calc(100vh - 16rem) !important;
          min-height: 14rem;
        }
        @media (max-width: 767.98px) {
          .sez-report-select { min-width: 100%; max-width: 100%; }
          .sez-report-actions { width: 100%; }
        }
        @media (max-width: 575.98px) {
          .sez-reports-page { padding: 6px !important; }
          .sez-reports-toolbar { padding: 6px !important; gap: 6px !important; }
          .sez-report-actions { gap: 6px !important; }
          .sez-range-select { flex: 1 1 auto; width: auto; min-width: 0; }
          .sez-range-select .form-select { padding-right: 1.5rem; }
          .sez-report-actions > .btn { flex: 0 0 auto; }
        }
      `}</style>

      <div
        className="sez-reports-toolbar position-relative d-flex flex-wrap flex-shrink-0 justify-content-between align-items-center gap-2 px-2 py-1 border-bottom"
        style={{ zIndex: 10, minHeight: 48 }}
      >
        <div className="sez-report-select d-flex align-items-center gap-2">
          <h5 className="mb-0 text-dark text-nowrap">Reports</h5>
          <Form.Select
            aria-label="Report"
            className="d-none d-md-block text-primary fs--1"
            size="sm"
            value={report.id}
            onChange={(event) =>
              onReportChange(event.target.value as ReportId)
            }
            style={{ cursor: "pointer" }}
            title={report.title}
          >
            {reports.map((option) => (
              <option key={option.id} value={option.id}>
                {option.title}
              </option>
            ))}
          </Form.Select>
          <Form.Select
            aria-label="Report"
            className="d-md-none text-primary fs--1"
            size="sm"
            value={report.id}
            onChange={(event) =>
              onReportChange(event.target.value as ReportId)
            }
            style={{ cursor: "pointer" }}
            title={report.title}
          >
            {reports.map((option) => (
              <option key={option.id} value={option.id}>
                {reportLabel(option.title)}
              </option>
            ))}
          </Form.Select>
        </div>
        <div className="sez-report-actions d-flex align-items-center justify-content-end gap-2 flex-shrink-0 ms-auto">
          <RangeSelect value={range} onChange={onRangeChange} />
          <Button
            variant="falcon-primary"
            size="sm"
            disabled={exporting}
            onClick={exportReport}
          >
            <FontAwesomeIcon
              icon={faFileExcel}
              className="me-2 text-warning"
            />
            {exporting ? "Preparing…" : "XLSX"}
          </Button>
        </div>
      </div>

      {hasRows ? (
        <div
          className={`sez-report-table position-relative flex-grow-1 ${
            report.id === "top-payors" ? "sez-top-payors-table" : ""
          } ${
            report.columns.some(
              (column) => column.accessor === "certificate_issue_date"
            )
              ? "sez-certificate-report"
              : ""
          }`}
          style={{
            minHeight: 0,
            zIndex: 1,
            "--report-column-count": Math.max(report.columns.length - 1, 1),
            "--report-other-column-width": `${
              82 / Math.max(report.columns.length - 2, 1)
            }%`,
          } as CSSProperties}
        >
          <DataTable
            key={report.id}
            gridName={report.gridName}
            columns={report.columns}
            data={report.rows}
            shortPrepend={`${report.id}_`}
            perPage={report.serverPagination ? report.pageSize : 20}
            sortable={false}
            searchable={false}
            showColumnFilter={true}
            selection={false}
            pagination={
              report.serverPagination
                ? report.totalPages > 1
                : report.rows.length > 20
            }
            numberPagination={report.serverPagination}
            currentPage={report.page}
            maxPage={report.totalPages}
            hasNext={report.hasNext}
            onChangePageNumber={report.onPageChange}
            showExport={false}
            localFilter={true}
            uniqueFilterKey={report.uniqueFilterKey}
            autoHeight={false}
            tableMinHeight={480}
            staticHiddenCols={["id"]}
          />
        </div>
      ) : (
        <div className="position-relative flex-grow-1 d-flex align-items-center justify-content-center text-muted fs--1">
          No records for the selected period
        </div>
      )}
    </div>
  );
}

function RangeSelect({
  value,
  onChange,
}: {
  value: RangeKey;
  onChange: (value: RangeKey) => void;
}) {
  return (
    <div className="sez-range-select btn btn-falcon-primary btn-sm position-relative d-inline-flex align-items-center p-0">
      <FontAwesomeIcon
        icon={faCalendarAlt}
        className="position-absolute start-0 ms-2 text-primary"
        style={{ pointerEvents: "none", zIndex: 1 }}
      />
      <Form.Select
        size="sm"
        value={value}
        onChange={(event) => onChange(event.target.value as RangeKey)}
        className="border-0 bg-transparent text-primary fw-bold shadow-none ps-4"
        style={{ cursor: "pointer" }}
      >
        {RANGE_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Form.Select>
    </div>
  );
}
